import clsx from 'clsx';
import React, {useEffect, useRef, useState} from 'react';

import type {
  MountedRemote,
  RemoteConfig,
  RemoteError,
  RemoteMount as RemoteMountFn,
  RemoteMountProps,
  ShellRouter,
} from '@wandb/forge-shell-contract';

import type {RemoteName} from './shellConfig';
import type {Surface} from './surface';
import styles from './RemoteMount.module.css';
import {ErrorState} from './ErrorState';
import {ShellLoader} from './ShellLoader';
import {
  analyticsForRemote,
  uninstallSegmentGlobalProxy,
} from './analytics/segment';
import {useRemotePatchHeader} from './header/ShellHeaderContext';
import {useRemoteBroadcastUrlAccount} from './account/UrlAccountBroadcastContext';
import {RemoteConfigLoadError} from './remoteLoader';
import {
  identityLoginUrl,
  redirectToIdentityLogin,
} from './identity/loginRedirect';

export type RemoteMountStatus =
  | 'loading'
  | 'ready'
  | 'error'
  | 'config-error'
  | 'redirecting';

function handleRemoteError(
  remoteName: RemoteName,
  error: RemoteError,
  redirectedRef: React.MutableRefObject<boolean>,
  setStatus: React.Dispatch<React.SetStateAction<RemoteMountStatus>>,
  origins: Partial<Record<Surface, string>> | undefined,
  next: string | undefined,
  redirectToLogin: typeof redirectToIdentityLogin
) {
  console.error(`${remoteName} remote failed to load`, error);
  if (error.kind === 'unauthenticated') {
    if (redirectedRef.current) {
      return;
    }
    if (identityLoginUrl(origins, next)) {
      redirectedRef.current = true;
      setStatus('redirecting');
      redirectToLogin(origins, next);
      return;
    }
  }
  setStatus('error');
}

type Props = {
  remote?: RemoteConfig;
  remoteName: RemoteName;
  router: ShellRouter;
  load: (remote: RemoteConfig) => Promise<RemoteMountFn>;
  label: string;
  origins?: Partial<Record<Surface, string>>;
  next?: string;
  redirectToIdentityLogin?: typeof redirectToIdentityLogin;
  selectedAccount: RemoteMountProps['selectedAccount'];
  selectAccount?: RemoteMountProps['selectAccount'];
  aria?: RemoteMountProps['aria'];
};

export const RemoteMount = ({
  remote,
  remoteName,
  router,
  load,
  label,
  origins,
  next,
  redirectToIdentityLogin: redirectToLogin = redirectToIdentityLogin,
  selectedAccount,
  selectAccount,
  aria,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<MountedRemote | undefined>(undefined);
  const redirectedRef = useRef(false);
  const [status, setStatus] = useState<RemoteMountStatus>(
    remote ? 'loading' : 'error'
  );
  const [handoff, setHandoff] = useState(false);

  const onError: NonNullable<RemoteMountProps['onError']> = error => {
    setHandoff(false);
    handleRemoteError(
      remoteName,
      error,
      redirectedRef,
      setStatus,
      origins,
      next,
      redirectToLogin
    );
  };

  // Memoized per name inside, so this is stable across renders.
  const analytics = analyticsForRemote(remoteName);
  const patchHeader = useRemotePatchHeader(remote != null);
  const broadcastUrlAccount = useRemoteBroadcastUrlAccount(remote != null);

  const props: RemoteMountProps = {
    router,
    selectedAccount,
    selectAccount,
    aria,
    onError,
    analytics,
    assetBase: remote?.assetBase,
    patchHeader,
    broadcastUrlAccount,
  };
  const latestPropsRef = useRef<RemoteMountProps>(props);

  useEffect(() => {
    if (!remote || !containerRef.current) {
      return;
    }

    let active = true;
    void load(remote)
      .then(mount => {
        if (!active || !containerRef.current) {
          return;
        }
        setStatus('ready');
        setHandoff(true);
        instanceRef.current = mount(
          containerRef.current,
          latestPropsRef.current!
        );
      })
      .catch(nextError => {
        console.error(`${remoteName} remote failed to load`, nextError);
        if (nextError instanceof RemoteConfigLoadError) {
          setStatus('config-error');
          return;
        }
        setStatus('error');
      });

    return () => {
      active = false;
      instanceRef.current?.unmount();
      instanceRef.current = undefined;
      /*
        After the remote's own unmount, so anything it tracks on the way out is
        still attributed to it. A no-op unless the remote asked for the proxy via
        `analytics.installSegmentGlobalProxy()`.
      */
      uninstallSegmentGlobalProxy();
    };
  }, [remote, load, remoteName]);

  const onOverlayAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === styles.handoffFade) {
      setHandoff(false);
    }
  };

  useEffect(() => {
    const nextProps: RemoteMountProps = {
      router,
      selectedAccount,
      selectAccount,
      aria,
      analytics,
      assetBase: remote?.assetBase,
      patchHeader,
      broadcastUrlAccount,
      onError: error => {
        setHandoff(false);
        handleRemoteError(
          remoteName,
          error,
          redirectedRef,
          setStatus,
          origins,
          next,
          redirectToLogin
        );
      },
    };
    latestPropsRef.current = nextProps;
    instanceRef.current?.update(nextProps);
  }, [
    router,
    selectedAccount,
    selectAccount,
    aria,
    analytics,
    remoteName,
    patchHeader,
    broadcastUrlAccount,
    origins,
    next,
    redirectToLogin,
    remote?.assetBase,
  ]);

  return (
    <section aria-label={`${label} remote`} className={styles.section}>
      <div ref={containerRef} className={styles.container} />
      {(status === 'loading' || handoff) && (
        <div
          className={clsx(styles.state, handoff && styles.stateHandoff)}
          onAnimationEnd={onOverlayAnimationEnd}>
          <ShellLoader label={`Loading ${label}`} />
        </div>
      )}
      {status === 'error' && (
        <div className={styles.state}>
          <ErrorState title={`${label} couldn't be loaded.`} />
        </div>
      )}
      {status === 'config-error' && (
        <div className={styles.state}>
          <ErrorState title={`${label} couldn't be loaded.`} />
        </div>
      )}
      {status === 'redirecting' && (
        <div className={styles.state} role="status">
          Redirecting to login…
        </div>
      )}
    </section>
  );
};
